/**
 * Client-facing DTO projection (sharing redesign · Phase 5, Agent 1).
 *
 * The only function allowed to produce a `SharePolicyClientDto` — the only
 * policy shape permitted to cross to a client (worker share manifest, and
 * eventually the client-web gallery, Phase 6). See `contracts.ts` for the
 * shape's redaction guarantee.
 */

import {
  shareClientCapabilities,
  originalCopiesFor,
  preparedCopiesFor,
  type SharePolicyClientDto,
  type SharePolicy,
  type OriginalCopy,
  type PreparedCopy,
} from './contracts'

export interface SharePolicyClientDtoOptions {
  /** Epoch millis the link expires at. Computed by the caller from publish time + expiryDays. */
  expiresAt: number
  /** Defaults to the policy's own version. */
  policyVersion?: number
}

/**
 * Project a normalized policy into the client-safe DTO. Carries no passcode
 * secret, no path, no idempotency key, no job detail, no membership source
 * identity, and no recipient data — see `clientDto.test.ts`'s redaction walk.
 */
export function toSharePolicyClientDto(
  policy: SharePolicy,
  opts: SharePolicyClientDtoOptions
): SharePolicyClientDto {
  const prepared = policy.downloads.allowDownloads
    ? preparedCopiesFor(policy.downloads.preparedFormat)
    : []
  const originals = policy.originals.allowOriginals ? originalCopiesFor(policy.originals.kind) : []

  const dto: SharePolicyClientDto = {
    policyVersion: opts.policyVersion ?? policy.version,
    access: policy.access.mode,
    passcodeRequired: policy.access.mode === 'anyone' && policy.access.passcode !== null,
    can: shareClientCapabilities(policy.version, policy.clientMode),
    downloads: {
      prepared,
      originals,
    },
    expiresAt: opts.expiresAt,
  }

  return Object.freeze(dto)
}

/**
 * True when the client should see a download control at all — a permitted
 * prepared copy, or originals. Mirrors `hasDownloadSurface` in `contracts.ts`
 * but reads it off the already-projected DTO, so a client-side component
 * never has to see the full policy to decide whether to render the control.
 */
export function clientDownloadSurfaceVisible(dto: SharePolicyClientDto): boolean {
  return dto.downloads.prepared.length > 0 || dto.downloads.originals.length > 0
}

const PREPARED = new Set<PreparedCopy>(['web', 'largest'])
const ORIGINALS = new Set<OriginalCopy>(['jpg', 'raw'])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Validate the frozen DTO at the network boundary. A present-but-malformed or
 * future policy is never widened through legacy flags.
 */
export function readClientPolicyDto(value: unknown): SharePolicyClientDto | null {
  if (
    !isRecord(value) ||
    (value.policyVersion !== 0 && value.policyVersion !== 1 && value.policyVersion !== 2)
  )
    return null
  if (value.access !== 'anyone' && value.access !== 'invited') return null
  if (
    typeof value.passcodeRequired !== 'boolean' ||
    typeof value.expiresAt !== 'number' ||
    !Number.isFinite(value.expiresAt)
  ) {
    return null
  }
  if (!isRecord(value.can) || !isRecord(value.downloads)) return null
  const can = value.can
  const downloads = value.downloads

  const abilities = ['view', 'select', 'comment', 'approveDelivery'] as const
  if (abilities.some((ability) => typeof can[ability] !== 'boolean')) return null
  if (!Array.isArray(downloads.prepared) || !Array.isArray(downloads.originals)) {
    return null
  }
  if (downloads.prepared.some((copy) => !PREPARED.has(copy as PreparedCopy))) return null
  if (downloads.originals.some((copy) => !ORIGINALS.has(copy as OriginalCopy))) return null

  return Object.freeze({
    policyVersion: value.policyVersion,
    access: value.access,
    passcodeRequired: value.passcodeRequired,
    can: Object.freeze({
      view: can.view as boolean,
      select: can.select as boolean,
      comment: can.comment as boolean,
      approveDelivery: can.approveDelivery as boolean,
    }),
    downloads: Object.freeze({
      prepared: Object.freeze([...(downloads.prepared as PreparedCopy[])]),
      originals: Object.freeze([...(downloads.originals as OriginalCopy[])]),
    }),
    expiresAt: value.expiresAt as number,
  })
}
